import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { galleryImages } from "../data/images";

const FILTERS = [
  { id: "all", label: { de: "Alle", en: "All" } },
  { id: "parquet", label: { de: "Parkett", en: "Parquet" } },
  { id: "vinyl", label: { de: "Vinyl", en: "Vinyl" } },
  { id: "laminate", label: { de: "Laminat", en: "Laminate" } },
  { id: "carpet", label: { de: "Teppich", en: "Carpet" } },
  { id: "design", label: { de: "Design", en: "Design" } },
];

export const ProjectGallery = ({ hideHeader = false }) => {
  const { t, lang } = useLang();
  const [active, setActive] = useState("all");

  const items = useMemo(
    () => (active === "all" ? galleryImages : galleryImages.filter((g) => g.category === active)),
    [active]
  );

  return (
    <section id="projects" className="relative py-24 lg:py-32 bg-[#EFEBE6]" data-testid="projects-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col gap-6 mb-12">
          {!hideHeader && (
          <>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A5A44]" data-testid="projects-eyebrow">
            — {t("gallery.eyebrow")}
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl leading-[0.95]" data-testid="projects-title">
              {t("gallery.title")}
            </h2>
            <p className="text-base text-[#4A4A4A] max-w-md leading-relaxed">{t("gallery.subtitle")}</p>
          </div>
          </>
          )}

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 pt-4">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setActive(f.id)}
                className={`px-4 py-2 rounded-full text-sm border transition-all ${
                  active === f.id
                    ? "bg-[#1A1A1A] text-[#F9F8F6] border-[#1A1A1A]"
                    : "border-[#1A1A1A]/25 text-[#1A1A1A] hover:border-[#1A1A1A]"
                }`}
                data-testid={`gallery-filter-${f.id}`}
              >
                {f.label[lang]}
              </button>
            ))}
          </div>
        </div>

        {/* Masonry-like grid */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => {
              const tall = i % 5 === 0 || i % 5 === 3;
              return (
                <motion.figure
                  layout
                  key={g.src}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.55, delay: (i % 6) * 0.05 }}
                  className={`zoom-wrap relative bg-[#1A1A1A] ${tall ? "row-span-2 aspect-[3/4]" : "aspect-[4/3]"}`}
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={g.src}
                    alt={g.label}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <figcaption className="absolute left-4 bottom-4 bg-[#F9F8F6]/90 backdrop-blur-md text-[#1A1A1A] text-xs font-medium px-3 py-1.5 rounded-full">
                    {g.label}
                  </figcaption>
                </motion.figure>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
